/**
 * AI-assisted search client -- calls the deployed fdp-search-worker
 * Cloudflare Worker (source: workers/fdp-search-worker/worker-site.js in
 * this repo, deployed separately via Cloudflare's GitHub-connected
 * Workers Builds, not by this site's own build).
 *
 * This is an optional fallback path, not a replacement for the existing
 * MiniSearch-based search in knowledgeBaseSearch.ts. It only fires when
 * MiniSearch's own top result is a weak match, and the caller is expected
 * to always fall back to the existing MiniSearch result when this
 * doesn't produce anything usable -- see needsAIAssist() and the
 * function contract below. This module never throws.
 */

// Public URL of the deployed Worker -- not a secret, safe to ship in the
// client bundle. Falls back to the real, currently-deployed Worker so
// this works out of the box; VITE_AI_SEARCH_WORKER_URL overrides it if
// the Worker is ever redeployed at a different URL.
export const WORKER_URL: string =
  import.meta.env.VITE_AI_SEARCH_WORKER_URL ||
  "https://fdp-search.kottalgi-2022.workers.dev";

// Raised from 2.5s: the worker can now write a short answer or a clarifying
// question, which takes longer than picking a tool ID. Search runs only when
// someone presses Enter and shows "Searching...", so waiting a few seconds
// for a genuinely better result is fine -- discarding it as a "failure"
// after 2.5s was not.
export const WORKER_TIMEOUT_MS = 6000;
// The Worker's own README suggested 3.0, calibrated against nothing --
// this site's actual MiniSearch scores (with its own field-boost and
// term-coverage reranking) run in the hundreds, not single digits.
// Measured directly against real queries before picking this number:
// "compress pdf" (exact match) scores ~261, "pdf compressor" (close
// synonym, still correct) ~70, while genuinely weak/wrong matches like
// "make my file smaller" -> Invitation Card Maker score ~94-117 --
// so raw score alone doesn't cleanly separate good matches from bad
// ones at any single cutoff (a correct match can score lower than a
// wrong one). Given AI-assist only ever prepends a result rather than
// replacing MiniSearch's own top pick, erring toward firing MORE often
// is low-risk -- worst case is a redundant second opinion alongside an
// already-correct result, not a wrong one replacing a right one.
export const AI_CONFIDENCE_THRESHOLD = 200;

/**
 * True when MiniSearch's own top score is below AI_CONFIDENCE_THRESHOLD
 * (or there were no results at all), the query is non-trivial (at least
 * 2 characters -- matches the Worker's own minimum, so a query that would
 * just get rejected there doesn't even get sent), OR the query is long
 * enough that it's very likely conversational/multi-step rather than a
 * short tool-name lookup.
 *
 * The score-only check alone isn't reliable across very different query
 * lengths -- confirmed directly, not assumed: a genuinely weak 14-word
 * query ("i want to trasfer the file to pdf and then go for singing it")
 * scored 874 on this site's actual MiniSearch index, far above
 * AI_CONFIDENCE_THRESHOLD (200), because longer queries accumulate score
 * from sheer word count regardless of match quality. The word-count
 * trigger below exists specifically so a query like that still reaches
 * AI-assist even when its raw score alone wouldn't. LONG_QUERY_WORD_COUNT
 * is set well above ordinary tool searches (which are almost always
 * 1-4 words) so it only fires for genuinely long, conversational input.
 */
const LONG_QUERY_WORD_COUNT = 6;

export function needsAIAssist(
  topMiniSearchScore: number | undefined,
  query: string,
): boolean {
  const q = query.trim();
  if (q.length < 2) return false;
  if (topMiniSearchScore === undefined || topMiniSearchScore < AI_CONFIDENCE_THRESHOLD) return true;
  const wordCount = q.split(/\s+/).filter(Boolean).length;
  return wordCount >= LONG_QUERY_WORD_COUNT;
}

export interface AISearchCandidate {
  id: string;
  name: string;
  description: string;
}

export interface AISearchResult {
  /** True only when the Worker actually produced this result -- false
   * for every failure path (network error, timeout, non-200, malformed
   * JSON, or an { error } response), so the caller can check this
   * instead of wrapping every call in its own try/catch. */
  fromAI: boolean;
  intent: "tool" | "workflow" | "clarify" | "answer" | "none";
  toolIds: string[];
  reply?: string;
  /** clarify only: the one short question to ask. */
  question?: string;
  /** clarify only: 2-4 answers, each leading to real candidate tool IDs. */
  options?: { label: string; toolIds: string[] }[];
  fallbackSearch: string;
  /** The Worker's own reason when it answered HTTP 200 but could not get
   * a usable AI result (e.g. "ai_unavailable" -- which includes the AI
   * provider rate-limiting us -- "parse_failed", "schema_invalid").
   * Absent for a genuine AI answer, including a genuine "no match". */
  workerError?: string;
}

const FAILURE_RESULT = (query: string): AISearchResult => ({
  fromAI: false,
  intent: "none",
  toolIds: [],
  fallbackSearch: query,
});

/**
 * Calls the Worker with the current query and MiniSearch's own top
 * candidates. Always resolves, never throws or rejects -- every failure
 * path (network error, timeout, non-200 response, malformed JSON, or an
 * { error } response body) resolves to the same safe fallback shape so
 * the caller can always fall through to the existing MiniSearch result
 * without needing its own try/catch.
 */
/** A short FAQ entry the site already found for this query. Sent so the model
 * can answer questions about the site from OUR wording, not from memory. */
export interface AISearchContext {
  q: string;
  a: string;
}

export async function searchWithAI(
  query: string,
  candidates: AISearchCandidate[],
  context: AISearchContext[] = [],
): Promise<AISearchResult> {
  if (candidates.length === 0) return FAILURE_RESULT(query);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WORKER_TIMEOUT_MS);

  try {
    const res = await fetch(WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, candidates, context: context.slice(0, 3) }),
      signal: controller.signal,
    });

    if (!res.ok) return FAILURE_RESULT(query);

    const data = await res.json();
    if (
      !data ||
      typeof data !== "object" ||
      "error" in data ||
      typeof data.intent !== "string" ||
      !Array.isArray(data.toolIds)
    ) {
      return FAILURE_RESULT(query);
    }

    // The Worker reports its own failures as HTTP 200 with an `_error`
    // field (and intent "none"), not as an HTTP error. Until this check
    // existed, a rate-limited or unparseable AI call was indistinguishable
    // from the model genuinely answering "no match" -- which made it
    // impossible to tell, in the search log, whether the AI was failing
    // or simply had nothing to offer.
    if (typeof data._error === "string") {
      return { ...FAILURE_RESULT(query), workerError: data._error };
    }

    // Defence in depth: the worker already validates all of this, but this
    // is the last stop before model-written text reaches the screen, so the
    // client re-checks the intent and re-applies the allow-list itself
    // rather than trusting the response shape.
    const known = new Set(candidates.map((c) => c.id));
    const intents = ["tool", "workflow", "clarify", "answer", "none"];
    if (!intents.includes(data.intent)) return FAILURE_RESULT(query);
    const onlyKnown = (ids: unknown): string[] =>
      Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string" && known.has(id)).slice(0, 4) : [];
    const options = Array.isArray(data.options)
      ? data.options
          .map((o: { label?: unknown; toolIds?: unknown }) => ({
            label: typeof o?.label === "string" ? o.label.slice(0, 40) : "",
            toolIds: onlyKnown(o?.toolIds),
          }))
          .filter((o: { label: string; toolIds: string[] }) => o.label && o.toolIds.length > 0)
          .slice(0, 4)
      : undefined;

    return {
      fromAI: true,
      intent: data.intent,
      toolIds: data.toolIds,
      question: typeof data.question === "string" ? data.question.slice(0, 140) : undefined,
      options,
      reply: typeof data.reply === "string" ? data.reply.slice(0, 420) : undefined,
      fallbackSearch:
        typeof data.fallbackSearch === "string" ? data.fallbackSearch : query,
    };
  } catch {
    // Network error, abort/timeout, or JSON parse failure -- all treated
    // the same as any other failure to reach or parse a usable response.
    return FAILURE_RESULT(query);
  } finally {
    clearTimeout(timer);
  }
}
